import { useEffect, useState, useRef } from 'react';
import api from '../../api/client';
import { useFincaId } from '../../context/FincaContext';

const MODOS = [
  { value: 'voleo',       label: 'Voleo',       color: '#9333EA' },
  { value: 'cronograma',  label: 'Cronograma',  color: '#0D47A1' },
  { value: 'sonico',      label: 'Automático',  color: '#6A1B9A' },
  { value: 'hibrido',     label: 'Híbrido',     color: '#EA580C' },
  { value: 'automatico',  label: 'Automático',  color: '#6A1B9A' },
];

const colorPorModo = Object.fromEntries(MODOS.map((m) => [m.value, m.color]));

export default function AlimentacionManual() {
  const fincaIdContext = useFincaId();
  const hoy = new Date().toISOString().split('T')[0];

  const [fecha, setFecha] = useState(hoy);
  const [sectorId, setSectorId] = useState('');
  const [piscinaFiltro, setPiscinaFiltro] = useState('');

  const [sectores, setSectores] = useState([]);
  const [filas, setFilas] = useState([]);
  const [balanceados, setBalanceados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [guardandoId, setGuardandoId] = useState(null);

  const inputsKgRef = useRef({});

  const cambiarDia = (delta) => {
    const [y, m, d] = fecha.split('-').map(Number);
    const nueva = new Date(Date.UTC(y, m - 1, d));
    nueva.setUTCDate(nueva.getUTCDate() + delta);
    const yyyy = nueva.getUTCFullYear();
    const mm = String(nueva.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(nueva.getUTCDate()).padStart(2, '0');
    setFecha(`${yyyy}-${mm}-${dd}`);
  };

  const irHoy = () => setFecha(new Date().toISOString().split('T')[0]);

  /* Cargar sectores */
  useEffect(() => {
    if (!fincaIdContext) return;
    (async () => {
      try {
        const { data } = await api.get(`/sectores/finca/${fincaIdContext}`);
        setSectores(data || []);
        if (data && data.length > 0 && !sectorId) setSectorId(data[0].id);
      } catch (err) {
        console.error(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaIdContext]);

  /* Cargar balanceados */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/alimentacion-manual/balanceados');
        setBalanceados(
          Array.isArray(data)
            ? data.map((b) => (typeof b === 'string' ? b : b.nombre)).filter(Boolean)
            : []
        );
      } catch (err) {
        console.error(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaIdContext]);

  /* Cargar filas cuando cambia fecha o sector */
  useEffect(() => {
    if (!fecha || !sectorId) {
      setFilas([]);
      return;
    }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha, sectorId]);

  const cargar = async () => {
    setCargando(true);
    setMensaje(null);
    try {
      const { data } = await api.get('/alimentacion-manual', {
        params: { fecha, sector_id: sectorId },
      });

      setFilas(data.map((r) => {
        // ✅ Regla: solo mostramos la fila con modo "Voleo" cuando aplica.
        //    - Si origen === 'biologo' y el biólogo marcó Voleo → mostramos Voleo
        //    - Si hay fila manual → mostramos lo que se guardó (asumimos Voleo)
        //    - Si el biólogo marcó otro modo (hibrido, automatico) → NO mostramos
        //      el modo aquí (queda vacío). La fila se muestra sin modo.
        const biologoEsVoleo = (r.biologo_modo || '').toLowerCase() === 'voleo';
        const modoMostrar = r.origen === 'manual'
          ? (r.modo_alimentacion || null)
          : (biologoEsVoleo ? 'voleo' : null);

        return {
          piscina_id: r.piscina_id,
          piscina_codigo: r.piscina_codigo,
          sector_nombre: r.sector_nombre,
          tipo_balanceado: r.tipo_balanceado || '',
          dieta_proyectada_kg: r.dieta_proyectada_kg != null ? String(r.dieta_proyectada_kg) : '',
          modo_alimentacion: modoMostrar,           // null si no aplica
          origen: r.origen || null,                 // 'manual' | 'biologo' | null
          biologo_modo: r.biologo_modo || null,
        };
      }));
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'Error al cargar' });
    } finally {
      setCargando(false);
    }
  };

  const actualizarFila = (piscinaId, campo, valor) => {
    setFilas((prev) => prev.map((f) =>
      f.piscina_id === piscinaId ? { ...f, [campo]: valor } : f
    ));
  };

  const guardarFila = async (piscinaId, saltarSiguiente = false) => {
    const fila = filas.find((f) => f.piscina_id === piscinaId);
    if (!fila) return;

    setGuardandoId(piscinaId);
    try {
      await api.post('/alimentacion-manual', {
        piscina_id: piscinaId,
        fecha,
        tipo_balanceado: fila.tipo_balanceado || null,
        dieta_proyectada_kg: fila.dieta_proyectada_kg === '' ? null : fila.dieta_proyectada_kg,
        // ✅ Siempre guardamos como 'voleo' porque esta pestaña es de alimentación manual
        modo_alimentacion: 'voleo',
      });

      // Ya se guardó → pasa a ser 'manual' y conserva el modo voleo
      setFilas((prev) => prev.map((f) =>
        f.piscina_id === piscinaId
          ? { ...f, origen: 'manual', modo_alimentacion: 'voleo' }
          : f
      ));

      setMensaje({ tipo: 'ok', texto: `${fila.piscina_codigo} guardado` });
      setTimeout(() => setMensaje(null), 1800);

      if (saltarSiguiente) {
        const visibles = filasFiltradas;
        const idx = visibles.findIndex((f) => f.piscina_id === piscinaId);
        const siguiente = visibles[idx + 1];
        if (siguiente) {
          const ref = inputsKgRef.current[siguiente.piscina_id];
          if (ref) {
            ref.focus();
            ref.select?.();
          }
        }
      }
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error al guardar' });
    } finally {
      setGuardandoId(null);
    }
  };

  const filasFiltradas = piscinaFiltro
    ? filas.filter((f) => f.piscina_id === piscinaFiltro)
    : filas;

  const inputBase = 'w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';
  const labelBase = 'text-[10px] font-medium text-gray-500';

  const fechaBonita = (() => {
    if (!fecha) return '';
    const [y, m, d] = fecha.split('-');
    return `${d}/${m}/${y}`;
  })();

  const filasDelBiologo = filas.filter((f) => f.origen === 'biologo').length;

  return (
    <div className="p-6">

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6 p-4">
        <div className="flex items-end gap-4 flex-wrap">

          <div className="flex flex-col min-w-[220px]">
            <span className={labelBase}>Fecha</span>
            <div className="flex items-center gap-1 mt-0.5">
              <button
                type="button"
                onClick={() => cambiarDia(-1)}
                className="px-2.5 py-1.5 bg-slate-700 text-white rounded-lg hover:bg-slate-800 text-xs font-bold"
                title="Día anterior"
              >
                ←
              </button>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className={`${inputBase} flex-1`}
              />
              <button
                type="button"
                onClick={() => cambiarDia(1)}
                className="px-2.5 py-1.5 bg-slate-700 text-white rounded-lg hover:bg-slate-800 text-xs font-bold"
                title="Día siguiente"
              >
                →
              </button>
              <button
                type="button"
                onClick={irHoy}
                className="px-2.5 py-1.5 border border-slate-400 text-slate-700 rounded-lg hover:bg-slate-100 text-[11px] font-medium"
                title="Ir a hoy"
              >
                Hoy
              </button>
            </div>
            <span className="text-[10px] text-gray-400 mt-1">{fechaBonita}</span>
          </div>

          <label className="block min-w-[180px]">
            <span className={labelBase}>Sector</span>
            <select
              value={sectorId}
              onChange={(e) => { setSectorId(e.target.value); setPiscinaFiltro(''); }}
              className={`mt-0.5 ${inputBase}`}
            >
              <option value="">-- Selecciona --</option>
              {sectores.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          </label>

          <label className="block min-w-[180px]">
            <span className={labelBase}>Piscina (filtro opcional)</span>
            <select
              value={piscinaFiltro}
              onChange={(e) => setPiscinaFiltro(e.target.value)}
              disabled={!sectorId}
              className={`mt-0.5 ${inputBase} disabled:bg-gray-50`}
            >
              <option value="">Todas</option>
              {filas.map((f) => (
                <option key={f.piscina_id} value={f.piscina_id}>{f.piscina_codigo}</option>
              ))}
            </select>
          </label>

          <div className="text-xs text-gray-500 italic mb-2">
            💡 Escribe kg y presiona <kbd className="px-1.5 py-0.5 bg-gray-100 border rounded text-[10px]">Enter</kbd> para guardar y saltar a la siguiente piscina.
          </div>

        </div>

        {filasDelBiologo > 0 && (
          <div className="mt-3 p-2 bg-purple-50 border border-purple-200 rounded text-xs text-purple-800 flex items-center gap-2">
            <span className="text-base">📋</span>
            <span>
              <strong>{filasDelBiologo}</strong> piscina{filasDelBiologo !== 1 ? 's' : ''} precargada{filasDelBiologo !== 1 ? 's' : ''} desde
              el <strong>Registro Biólogo</strong> porque el modo es <strong>Voleo</strong>.
              Edita si es necesario y presiona <kbd className="px-1 py-0.5 bg-white border rounded text-[10px]">Enter</kbd> para confirmar.
            </span>
          </div>
        )}
      </div>

      {mensaje && (
        <div className={`mb-4 p-3 rounded text-sm ${
          mensaje.tipo === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {mensaje.texto}
        </div>
      )}

      {!sectorId && (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          Selecciona una fecha y un sector.
        </div>
      )}

      {sectorId && cargando && (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">Cargando...</div>
      )}

      {sectorId && !cargando && filasFiltradas.length === 0 && (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          No hay piscinas activas en este sector.
        </div>
      )}

      {sectorId && !cargando && filasFiltradas.length > 0 && (
        <div className="bg-white rounded shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left px-4 py-2 font-semibold w-[130px]">Piscina</th>
                <th className="text-center px-4 py-2 font-semibold w-[120px]">Modo</th>
                <th className="text-left px-4 py-2 font-semibold">Balanceado</th>
                <th className="text-right px-4 py-2 font-semibold w-[180px]">Proyectada Blgo. (kg)</th>
                <th className="text-center px-4 py-2 font-semibold w-[80px]">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filasFiltradas.map((f) => {
                const esDelBiologo = f.origen === 'biologo';
                const modoAplica = f.modo_alimentacion === 'voleo';
                return (
                  <tr
                    key={f.piscina_id}
                    className={`border-t hover:bg-blue-50/30 ${
                      esDelBiologo ? 'bg-purple-50/40' : ''
                    }`}
                  >
                    <td className="px-4 py-2 font-medium">
                      <div className="flex items-center gap-2">
                        <span>{f.piscina_codigo}</span>
                        {esDelBiologo && (
                          <span
                            title={`Precargado desde Registro Biólogo (modo: ${f.biologo_modo})`}
                            className="text-[10px] bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded font-semibold"
                          >
                            📋 Biólogo
                          </span>
                        )}
                      </div>
                    </td>

                    {/* ✅ Modo: solo visualización, solo se muestra si es Voleo */}
                    <td className="px-4 py-2 text-center">
                      {modoAplica ? (
                        <span
                          className="inline-block px-2 py-0.5 rounded text-xs font-semibold"
                          style={{
                            color: colorPorModo['voleo'],
                            backgroundColor: '#F3E8FF',
                          }}
                          title="Modo de alimentación manual (Voleo)"
                        >
                          Voleo
                        </span>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>

                    <td className="px-4 py-2">
                      <select
                        value={f.tipo_balanceado}
                        onChange={(e) => actualizarFila(f.piscina_id, 'tipo_balanceado', e.target.value)}
                        onBlur={() => guardarFila(f.piscina_id, false)}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="">— Selecciona —</option>
                        {balanceados.map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </select>
                    </td>

                    <td className="px-4 py-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={f.dieta_proyectada_kg}
                        onChange={(e) => actualizarFila(f.piscina_id, 'dieta_proyectada_kg', e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            guardarFila(f.piscina_id, true);
                          }
                        }}
                        onBlur={() => guardarFila(f.piscina_id, false)}
                        ref={(el) => { if (el) inputsKgRef.current[f.piscina_id] = el; }}
                        placeholder="0.00"
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs text-right focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </td>

                    <td className="px-4 py-2 text-center">
                      {guardandoId === f.piscina_id ? (
                        <span className="text-[10px] text-gray-400">💾</span>
                      ) : f.origen === 'manual' ? (
                        <span className="text-[10px] text-green-600" title="Guardado en alimentación manual">✓</span>
                      ) : esDelBiologo ? (
                        <span className="text-[10px] text-purple-600" title="Pendiente de confirmar">📋</span>
                      ) : (
                        <span className="text-[10px] text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}